import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SummaryShipment } from '@src/models/returns';
import { CallInformation } from '@src/hooks/useMultiCallCreateReturn';
import {
  buildSummaryData,
  persistReturnSummaryinfo,
} from '@src/utils/finalizeReturnUtil';
import {
  findMatchingCreateReturnShipment,
  getItemsByHrtvShipment,
} from '@src/utils';
import { useReturnStore } from '@src/stores/returnStore';
import { useSummaryStore } from '@src/stores/summaryStore';

export const useNavigateToReturnSummary = () => {
  const navigate = useNavigate();
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const refundValues = useSummaryStore((state) => state.refundValues);
  const returnShipments = useReturnStore((state) => state.returnShipments);
  const selectedItems = useReturnStore((state) => state.selectedItems);

  const navigateToReturnSummary = useCallback(
    async (
      completedAPICalls: CallInformation[],
      hasDuplicateReturns: boolean,
    ) => {
      if (!orderDetailsV2) return;

      // Extract successful return create calls
      const returnCreateCalls = completedAPICalls
        .filter((call) => call.success)
        .map((call) => call.data);
      // Extract shipments from the successful return create calls
      const shipments = returnCreateCalls.flatMap(
        (call) => call.shipments || [],
      );
      // Filter returnShipments to only include successfully created shipments
      const successfulReturnShipments = returnShipments?.filter((shipment) => {
        // In-store returns (PHASE_0_SORIS) are generated client-side, always include them
        if (shipment.shipmentType === 'PHASE_0_SORIS') return true;
        if (shipment.shipmentType === 'SORIS') {
          // For SORIS shipments, check if any call included SORIS data
          return returnCreateCalls.some(
            (call) => call.sorisId && call.sorisQRCode,
          );
        }
        // For other shipment types, check if they exist in successful shipments from API
        return shipment.lineItems.some((lineItem) =>
          shipments?.some((successShipment) =>
            successShipment.itemIds.includes(lineItem.lineItemId),
          ),
        );
      });

      // Enrich returnShipments (IReturnShipments) with returnByDate and SORIS data
      const summaryShipments: SummaryShipment[] =
        successfulReturnShipments?.map((currentShipment) => {
          const itemsToReturn =
            getItemsByHrtvShipment(currentShipment, selectedItems) || [];
          const { shipmentId, shipmentType } = currentShipment;
          let returnByDate: string;
          let sorisFields;
          let upsLorpFields;

          if (currentShipment.shipmentType === 'PHASE_0_SORIS') {
            // For in-store items, use returnWindowEndDate from the first line item
            returnByDate = itemsToReturn[0]?.returnWindowEndDate || '';
          } else if (currentShipment.shipmentType === 'SORIS') {
            // For SORIS items, get data from the SORIS API call response
            const sorisCall = completedAPICalls.find(
              (call) => call.type === 'SORIS' && call.success,
            );
            returnByDate = sorisCall?.data?.returnByDate || '';
            sorisFields = {
              sorisId: sorisCall?.data?.sorisId,
              sorisQRCode: sorisCall?.data?.sorisQRCode,
            };
          } else {
            // For UPS/LORP items, use returnByDate from the Create Return API response
            const parentCall = returnCreateCalls.find((call) =>
              findMatchingCreateReturnShipment(currentShipment, call.shipments),
            );
            const matchedShipment = findMatchingCreateReturnShipment(
              currentShipment,
              parentCall?.shipments,
            );
            returnByDate = parentCall?.returnByDate || '';
            upsLorpFields = {
              trackingDetails: matchedShipment?.trackingDetails,
              returnTrackingNumbers: matchedShipment?.returnTrackingNumbers,
              boxQuantity: currentShipment.boxQuantity,
              maxBoxes: currentShipment.maxBoxes,
              lorpContactDetails: currentShipment.lorpContactDetails,
            };
          }

          return {
            shipmentId,
            shipmentType,
            returnByDate,
            itemsToReturn,
            ...sorisFields,
            ...upsLorpFields,
          };
        }) || [];

      const returnSummaryData = buildSummaryData(
        orderDetailsV2.orderNumber,
        orderDetailsV2.emailId,
        refundValues,
        summaryShipments,
        hasDuplicateReturns,
      );
      persistReturnSummaryinfo(returnSummaryData, navigate);
    },
    [navigate, orderDetailsV2, refundValues, returnShipments, selectedItems],
  );

  return navigateToReturnSummary;
};
